import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../contexts/LoaderContext";
import axios from 'axios';
import SummerYouAltLogo from "../../assets/summeryou-alt-logo.png"
import SummerYouLogo from "../../assets/summeryou-logo-cropped.png"

import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/20/solid'

const SignUp = () => {
    const {showLoader, hideLoader} = useLoader();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstName : '',
        lastName : '',
        email : '',
        username : '',
        password : ''
    });

    const [userNameValidation, setUserNameValidation] = useState({
        isValid : null,
        message : ''
    })

    const [emailValidation, setEmailValidation] = useState({
        isValid : null,
        message : ''
    })

    const [passwordValidation, setPasswordValidation] = useState({
        isValid : null,
        message : ''
    })

    const [confirmPassword, setConfirmPassword] = useState({
        value : '',
        isValid : null,
        message : ''
    })

    const passwordRules = [
        {
            title: "Minimum length of 8 characters",
            isValid: formData.password.length >= 8,
        },
        {
            title: "At least one number",
            isValid: /\d/.test(formData.password),
        },
        {
            title: "At least one of these special characters (e.g., ?!@#$%^&*,._)",
            isValid: /[?!@#$%^&*,._]/.test(formData.password),
        },
    ];

    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [pageLoading, setPageLoading] = useState(false);

    useEffect(() => {
        document.title = "Sign Up - SummerYou   ";
    });     

    const handleChange = (e) =>{
        e.preventDefault();
        const {value, name} = e.target;
        setFormData((prev) => ({
            ...prev,
            [name] : value
        }))
    }

    const handleEmailOnChange = (e) => {
        let {value} = e.target;
        value = value.replace(/\s/g, "");

        setEmailValidation({
            isValid : null,
            message : ''
        })  

        setFormData((prev) => ({
            ...prev,
            email : value
        }))

        const baseUrl = process.env.REACT_APP_SUMMERYOULAB_API_URL;
        axios.get(`${baseUrl}/check-availability?email=${value}`,{}, {timeout: 30000})
        .then((res) => {
            const data = res.data.data;

            if (!data.isAvailable) {
                setEmailValidation({    
                    isValid : false,
                    message : 'This email is already registered. Try logging in instead.'
                })
            }
            else {
                setEmailValidation({
                    isValid : true,
                    message : ''
                })
            }
        })
        .catch((error) => {
            if(error.status === 401) {setErrorMessage("")}
            else if (error.status === 400) 
            {
                const result = error.response.data;
                setEmailValidation({
                    isValid : result.error.info === null ? null : false,
                    message : result.error.info || ""
                })
            }
        })
        .finally (() => {})
        
    }

    const handleUsernameOnChange = (e) => {
        try 
        {
            let {value} = e.target;
            
            setUserNameValidation({
                isValid : null,
                message : ''
            })

            setFormData((prev) => ({
                ...prev,
                username : value
            }));
            
            if (value.length >= 8)
            {
                const baseUrl = process.env.REACT_APP_SUMMERYOULAB_API_URL;
                axios.get(`${baseUrl}/check-availability?username=${value}`,{}, {timeout: 30000})
                .then((res) => {
                    const data = res.data.data;

                    if (!data.isAvailable) {
                        setUserNameValidation({
                            isValid : false,
                            message : 'This username is already taken'
                        })
                    }
                    else {
                        setUserNameValidation({
                            isValid : true,
                            message : 'Valid'
                        })
                    }
                })
                .catch((error) => {
                    if(error.status === 401) {setErrorMessage("An error occured. Please try again later")}
                    else if (error.status === 400) 
                    {
                    const result = error.response.data;
                    setUserNameValidation({
                        isValid : false,
                        message : result.error.info || ""
                    })
                    }
                    setShowError(true);
                    })
                .finally (() => {})
            }
            
        } catch(error){}
    }

    const handlePasswordChange = (e) => {
        try {
            const { value } = e.target;

            // Update form data
            setFormData((prev) => ({
            ...prev,
            password: value,
            }));

            setPasswordValidation({
                isValid : null,
                message : ''
            })

            if (value.length > 0)
            {
                // Build rules with the latest input value
                const rules = [
                {
                    title: "Minimum length of 8 characters",
                    isValid: value.length >= 8,
                },
                {
                    title: "At least one number",
                    isValid: /\d/.test(value),
                },
                {
                    title: "At least one special character (e.g., ? ! @ # $ % ^ & * ( ) , . _ )",
                    isValid: /[?!@#$%^&*(),._]/.test(value),
                },
                ];

                const isPasswordValid = rules.every((rule) => rule.isValid);

                // Update validation state
                setPasswordValidation({
                    rules,        // keep all rules for UI display
                    isValid: isPasswordValid,
                });
            }
        } catch (error) {
            // console.error("Password validation error:", error);
        }
        };


    const handleConfirmPassword = (e) => {
        e.preventDefault();
        const {value} = e.target;
        const password = formData.password;

        /*Default*/
        setConfirmPassword({
            value : value,
            isValid : null,
            message : ''
        });

        if(password  && value)
        {
            if (password !== value)
            {
                setConfirmPassword({
                    value : value,
                    isValid : false,
                    message : 'Passwords do not match.'
                })
            }
            else {
                setConfirmPassword({
                    value : value,
                    isValid : true,
                    message : ''
                })
            }
        }
    }

    const handleSignUp = (e) => {
        try {
            e.preventDefault();

            /* No action proceeded when all fields are not yet valid */
            if (!emailValidation.isValid || !userNameValidation.isValid || !passwordValidation.isValid || !confirmPassword.isValid)
            {
                // setErrorMessage("An error occured. Please try again later");                    
                // setShowError(true);
                return;
            }
            showLoader();
            /* Proceed to save no error occurs */
            const baseUrl = process.env.REACT_APP_SUMMERYOULAB_API_URL;
            axios.post(`${baseUrl}/auth/signUp`, formData, {timeout: 30000})
                .then((res) => {
                    navigate("success")
                })
                .catch((error) => {
                    setErrorMessage("An error occured. Please try again later");                    
                    setShowError(true);
                })
                .finally (() => {
                    /* Clear form and validations */
                    setFormData({
                        email : '',
                        firstName : '',
                        lastName : '',
                        username : '',
                        password : ''
                    })

                    setEmailValidation({isValid : null, message : ''});
                    setUserNameValidation({isValid: null, message : ''});
                    setPasswordValidation({isValid: null, message : ''});
                    setConfirmPassword({value : '',isValid :null, message :''});

                })                         
        }
        catch (error) {
            setErrorMessage("An error occured. Please try again later");                    
            setShowError(true);
            alert(error);
        }

        hideLoader();
    }

    return (
        <>
            <div className="flex min-h-full flex-1 flex-col justify-center sm:px-6 lg:px-8 font-inter">
                <div className="flex min-h-full flex-1 flex-col justify-center  ">
                    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-[480px]">
                        
                        <div className="mt-8 sm:mx-auto sm:w-full sm:w-[480px]">
                            
                            <div className="bg-white px-6 py-10 sm:shadow sm:rounded-lg sm:px-12">
                                <div className="flex justify-center">
                                    <img src={SummerYouLogo} className="my-5 mx-5 sm:mx-0 w-auto h-8"></img>
                                </div>
                                {/* <div className="my-3 text-mute">
                                    <p>Sign up for full access to our services. </p>
                                </div> */}
                                <form onSubmit={handleSignUp} className="gap-5 grid grid-cols-2" action="#" method="POST">
                                    <div className="col-span-2 sm:col-span-1">
                                        <label htmlFor="lastName" className="block text-sm font-semibold leading-6 text-gray-900">
                                            First Name
                                        </label>
                                        <div className="mt-2">
                                            <input 
                                                type='text'
                                                name='firstName'
                                                onChange={handleChange}
                                                value={formData.firstName}
                                                required
                                                className="block w-full rounded-md border-0 py-2 px-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 required:border-pink-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            ></input>
                                        </div>
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                         <label htmlFor="lastName" className="block text-sm font-semibold leading-6 text-gray-900">
                                            Last Name 
                                        </label>
                                        <div className="mt-2">
                                            <input 
                                                type='text'
                                                name='lastName'
                                                onChange={handleChange}
                                                value={formData.lastName}
                                                required
                                                className="block w-full rounded-md border-0 py-2 px-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 required:border-pink-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            ></input>
                                        </div>                                       
                                    </div>
                                    <div className="col-span-2">
                                        <label htmlFor="email" className="block text-sm font-semibold leading-6 text-gray-900 ">
                                        E-mail Address
                                        </label>
                                        <div className="relative mt-2 rounded-md shadow-sm">
                                            <input 
                                                type='email'
                                                name='email'
                                                value={formData.email}
                                                onChange={handleEmailOnChange}
                                                required
                                                placeholder="you@example.com"
                                                className={`block w-full rounded-md border-0 py-1.5 pr-10 
                                                ${emailValidation.isValid === null 
                                                    ? 'text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2' : 
                                                    (emailValidation.isValid ? 'text-gray-900 ring-1 ring-inset ring-teal-500 focus:ring-2 focus:ring-teal-500' : 'text-red-900 focus:ring-2 focus:ring-red-300 ring-1 ring-inset ring-red-300')  } 
                                                sm:text-sm sm:leading-6`}
                                                ></input>
                                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                {emailValidation.isValid !== null && (
                                                <span
                                                    className={`transition-opacity duration-300 ${
                                                    emailValidation.isValid ? "opacity-100" : "opacity-100"
                                                    }`}
                                                >
                                                    {emailValidation.isValid === true ? (
                                                    <CheckCircleIcon className="h-5 w-5 text-teal-500" aria-label="Valid username" />
                                                    ) : (
                                                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-label="Invalid username" />
                                                    )}
                                                </span>
                                                )}
                                            </div>
                                        </div> 
                                        {!emailValidation.isValid && (<p className="text-error mt-1">{emailValidation.message}</p>)}

                                    </div>
                                    
                                    <div className="col-span-2">
                                        <label htmlFor="username" className="block text-sm font-semibold leading-6 text-gray-900 ">
                                        Username
                                        </label>
                                        <div className="relative mt-2 rounded-md shadow-sm">
                                            <input
                                            type='text'
                                            name='username'
                                            required
                                            value={formData.username}
                                            onChange={handleUsernameOnChange}
                                            className={`block w-full rounded-md border-0 py-1.5 pr-10 
                                                ${userNameValidation.isValid === null 
                                                    ? 'text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2' : 
                                                    (userNameValidation.isValid ? 'text-gray-900 ring-1 ring-inset ring-teal-500 focus:ring-2 focus:ring-teal-500' : 'text-red-900 focus:ring-2 focus:ring-red-300 ring-1 ring-inset ring-red-300')  } 
                                                sm:text-sm sm:leading-6`}
                                            maxLength={`15`}
                                            aria-invalid="true"
                                            aria-describedby="email-error"
                                            />
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                {userNameValidation.isValid !== null && (
                                                <span
                                                    className={`transition-opacity duration-300 ${
                                                    userNameValidation.isValid ? "opacity-100" : "opacity-100"
                                                    }`}
                                                >
                                                    {userNameValidation.isValid === true ? (
                                                    <CheckCircleIcon className="h-5 w-5 text-teal-500" aria-label="Valid username" />
                                                    ) : (
                                                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-label="Invalid username" />
                                                    )}
                                                </span>
                                                )}

                                            </div>
                                        </div>
                                        {!userNameValidation.isValid && (<p className="text-error mt-1">{userNameValidation.message}</p>)}
                                    </div>

                                    <div className="col-span-2">
                                        <label htmlFor="password" className="block text-sm font-semibold leading-6 text-gray-900 ">
                                        Password
                                        </label>
                                        <div className="relative mt-2 my-1 rounded-md shadow-sm">
                                            <input 
                                                type='password'
                                                name='password'
                                                value={formData.password}
                                                onChange={handlePasswordChange}
                                                required
                                                className={`block w-full rounded-md border-0 py-1.5 pr-10 
                                                ${passwordValidation.isValid === null 
                                                    ? 'text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2' : 
                                                    (passwordValidation.isValid ? 'text-gray-900 ring-1 ring-inset ring-teal-500 focus:ring-2 focus:ring-teal-500' : 'text-red-900 focus:ring-2 focus:ring-red-300 ring-1 ring-inset ring-red-300')  } 
                                                sm:text-sm sm:leading-6`}
                                            />
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                {passwordValidation.isValid !== null && (
                                                <span
                                                    className={`transition-opacity duration-300 ${
                                                    passwordValidation.isValid ? "opacity-100" : "opacity-100"
                                                    }`}
                                                >
                                                    {passwordValidation.isValid === true ? (
                                                    <CheckCircleIcon className="h-5 w-5 text-teal-500" aria-label="Valid username" />
                                                    ) : (
                                                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-label="Invalid username" />
                                                    )}
                                                </span>
                                                )}

                                            </div>
                                        </div>
                                        <ul className="list-none">
                                            {passwordRules.map((rule, i) => (
                                                <li key={i} className={rule.isValid ? "text-success" : "text-error"}>
                                                    {rule.isValid ? "✔" : "✖"} {rule.title}
                                                </li>
                                            ))}
                                        </ul>

                                    </div>

                                    <div className="col-span-2">
                                        <label htmlFor="confirmPassword" className="block text-sm font-semibold leading-6 text-gray-900 ">
                                        Confirm Password
                                        </label>
                                        <div className="relative mt-2 rounded-md shadow-sm">
                                            <input 
                                                type='password'
                                                name='confirmPassword'
                                                value={confirmPassword.value}
                                                onChange={handleConfirmPassword}
                                                required
                                                className={`block w-full rounded-md border-0 py-1.5 pr-10 
                                                    ${confirmPassword.isValid === null 
                                                        ? 'text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2' : 
                                                        (confirmPassword.isValid ? 'text-gray-900 ring-1 ring-inset ring-teal-500 focus:ring-2 focus:ring-teal-500' : 'text-red-900 focus:ring-2 focus:ring-red-300 ring-1 ring-inset ring-red-300')  } 
                                                    sm:text-sm sm:leading-6`}
                                            ></input>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                {confirmPassword.isValid !== null && (
                                                <span
                                                    className={`transition-opacity duration-300 ${
                                                    confirmPassword.isValid ? "opacity-100" : "opacity-100"
                                                    }`}
                                                >
                                                    {confirmPassword.isValid === true ? (
                                                    <CheckCircleIcon className="h-5 w-5 text-teal-500" aria-label="Valid username" />
                                                    ) : (
                                                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-label="Invalid username" />
                                                    )}
                                                </span>
                                                )}

                                            </div>
                                        </div> 
                                        {!confirmPassword.isValid && (<p className="text-error mt-1">{confirmPassword.message}</p>)}

                                    </div>

                                    <div className="col-span-2">
                                        <button 
                                            type='submit'
                                            className="block w-full rounded-md border-1 py-2.5 px-2.5 
                                            bg-black text-white font-bold text-sm
                                            text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300  focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6"
                                        >
                                            Sign Up Now
                                        </button>                                        
                                    </div>
                                    
                                </form>

                                <p className="text-xs my-2">Already have an account? <a href="/signIn" className="underline text-blue-500">Sign in here</a></p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default SignUp;